import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as countries from "i18n-iso-countries";

type CountryLocaleData = Parameters<typeof countries.registerLocale>[0];

const localeLoaders: Record<string, () => Promise<CountryLocaleData>> = {
  sv: () => import("i18n-iso-countries/langs/sv.json").then((m) => m.default),
  en: () => import("i18n-iso-countries/langs/en.json").then((m) => m.default),
  de: () => import("i18n-iso-countries/langs/de.json").then((m) => m.default),
  fr: () => import("i18n-iso-countries/langs/fr.json").then((m) => m.default),
};

const pendingLocales = new Map<string, Promise<string | undefined>>();
const registeredLocales = new Set<string>();

const countryLocaleFor = (language: string | undefined) => {
  const base = language?.split("-")[0] ?? "en";
  return base in localeLoaders ? base : "en";
};

/** Registers the country-name table for a language, loading it on first use. */
export const loadCountryNames = (
  language: string | undefined,
): Promise<string | undefined> => {
  const locale = countryLocaleFor(language);
  const pending =
    pendingLocales.get(locale) ??
    localeLoaders[locale]()
      .then((data) => {
        countries.registerLocale(data);
        registeredLocales.add(locale);
        return locale;
      })
      .catch(() => {
        pendingLocales.delete(locale);
        return undefined;
      });
  pendingLocales.set(locale, pending);
  return pending;
};

/** The country-name locale for a language, once its table is registered. */
export const loadedCountryLocale = (language: string | undefined) => {
  const locale = countryLocaleFor(language);
  return registeredLocales.has(locale) ? locale : undefined;
};

/**
 * The current language's country-name locale, or `undefined` while its table
 * is still loading. Re-renders the caller once it arrives.
 */
export const useCountryNamesLocale = () => {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const [, setLoaded] = useState<string>();

  useEffect(() => {
    let active = true;
    void loadCountryNames(language).then((locale) => {
      if (active) setLoaded(locale);
    });
    return () => {
      active = false;
    };
  }, [language]);

  return loadedCountryLocale(language);
};

/** Localized country name, or the code itself until the names are loaded. */
export const getCountryName = (code: string, locale: string | undefined) => {
  if (!code) return "";
  return (locale && countries.getName(code, locale)) || code;
};
